"use client";

import { Button } from "@/components/ui/button";
import { useTour } from "@/context/TourContext";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { LuCheck } from "react-icons/lu";
import { getTourInfo } from "./tourDefinitions";

interface SingleStepTooltipProps {
  className?: string;
}

export const SingleStepTooltip: React.FC<SingleStepTooltipProps> = () => {
  const { tourState, completeTour } = useTour();
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);

  const currentStep = tourState.steps[tourState.currentStep];

  // Resetear estado de expansión cuando cambie el paso del tour
  useEffect(() => {
    setIsDetailsExpanded(false);
  }, [tourState.currentStep]);

  // Handle keyboard events
  useEffect(() => {
    if (!tourState.isActive || !currentStep) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" || event.key === "ArrowRight") {
        event.preventDefault();
        completeTour();
      } else if (event.key === "Escape") {
        event.preventDefault();
        completeTour();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [tourState.isActive, currentStep, completeTour]);

  // Find and highlight the target element
  useEffect(() => {
    if (!tourState.isActive || !currentStep) return;

    // Set transitioning state when step changes
    setIsTransitioning(true);
    setTargetElement(null);
    setSpotlightRect(null);

    const timer = setTimeout(() => {
      const element = document.querySelector(
        `[data-tour="${currentStep.target}"]`
      ) as HTMLElement;

      if (element) {
        // Verify the element matches the current step
        const elementTourId = element.getAttribute("data-tour");

        if (elementTourId === currentStep.target) {
          setTargetElement(element);
          const rect = element.getBoundingClientRect();
          setSpotlightRect(rect);
          setIsTransitioning(false);

          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "center",
          });
        } else {
          setTargetElement(null);
          setSpotlightRect(null);
          setIsTransitioning(false);
        }
      } else {
        setTargetElement(null);
        setSpotlightRect(null);
        setIsTransitioning(false);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [tourState.isActive, tourState.currentStep, currentStep]);

  // Update spotlight position on scroll/resize
  useEffect(() => {
    if (!targetElement || !tourState.isActive) return;

    const updatePosition = () => {
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        setSpotlightRect(rect);
      }
    };

    window.addEventListener("scroll", updatePosition, { passive: true });
    window.addEventListener("resize", updatePosition, { passive: true });

    return () => {
      window.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
    };
  }, [targetElement, tourState.isActive]);

  if (!tourState.isActive || !currentStep) return null;

  const getTooltipPosition = () => {
    if (!spotlightRect || isTransitioning) {
      return {
        top: "-9999px",
        left: "-9999px",
        opacity: 0,
      };
    }

    const { top, left, width, height } = spotlightRect;
    const placement = currentStep.placement || "bottom-center";

    const padding = 20;
    const tooltipWidth = 400; // Increased width for better visibility
    const tooltipHeight = 250; // Estimated height

    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Helper function to ensure popover stays within viewport
    const constrainToViewport = (
      x: number,
      y: number,
      customWidth?: number
    ) => {
      const widthToUse = customWidth || tooltipWidth;
      const constrainedX = Math.max(
        padding,
        Math.min(x, viewportWidth - widthToUse - padding)
      );
      const constrainedY = Math.max(
        padding,
        Math.min(y, viewportHeight - tooltipHeight - padding)
      );
      return { x: constrainedX, y: constrainedY };
    };

    switch (placement) {
      case "bottom-center": {
        // Calculate center position
        const centerX = left + width / 2;
        const bottomY = top + height + padding;

        // Check if there's enough space below
        if (bottomY + tooltipHeight > viewportHeight - padding) {
          // Position above if not enough space below
          const topY = top - tooltipHeight - padding;
          const { x, y } = constrainToViewport(centerX, topY, tooltipWidth);
          return {
            top: `${y}px`,
            left: `${x}px`,
            transform: "translateX(-50%)",
          };
        } else {
          // Position below
          const { x, y } = constrainToViewport(centerX, bottomY, tooltipWidth);
          return {
            top: `${y}px`,
            left: `${x}px`,
            transform: "translateX(-50%)",
          };
        }
      }
      case "top-center": {
        const centerX = left + width / 2;
        const topY = top - tooltipHeight - padding;
        const halfWidth = tooltipWidth / 2;

        // Calculate the actual position considering the transform
        const actualLeft = centerX - halfWidth;
        const actualRight = centerX + halfWidth;

        // Check if tooltip would go off screen and adjust accordingly
        let finalX = centerX;
        if (actualLeft < padding) {
          // Too far left, position at minimum padding
          finalX = padding + halfWidth;
        } else if (actualRight > viewportWidth - padding) {
          // Too far right, position at maximum right
          finalX = viewportWidth - padding - halfWidth;
        }

        // Ensure Y position is within bounds
        const finalY = Math.max(
          padding,
          Math.min(topY, viewportHeight - tooltipHeight - padding)
        );

        return {
          top: `${finalY}px`,
          left: `${finalX}px`,
          transform: "translateX(-50%)",
        };
      }
      case "right-center": {
        const rightX = left + width + padding;
        const centerY = top + height / 2;
        const { x, y } = constrainToViewport(rightX, centerY, tooltipWidth);
        return {
          top: `${y}px`,
          left: `${x}px`,
          transform: "translateY(-50%)",
        };
      }
      case "left-center": {
        const leftX = left - tooltipWidth - padding;
        const centerY = top + height / 2;
        const { x, y } = constrainToViewport(leftX, centerY, tooltipWidth);
        return {
          top: `${y}px`,
          left: `${x}px`,
          transform: "translateY(-50%)",
        };
      }
      default: {
        // Default to bottom-center
        const centerX = left + width / 2;
        const bottomY = top + height + padding;
        const { x, y } = constrainToViewport(centerX, bottomY, tooltipWidth);
        return {
          top: `${y}px`,
          left: `${x}px`,
          transform: "translateX(-50%)",
        };
      }
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="overlay"
        className="fixed inset-0 z-[10000] pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Dark overlay with cutout for spotlight */}
        <div
          className="absolute inset-0 bg-black/50"
          style={{
            clipPath: (() => {
              if (!spotlightRect) return "none";

              const padding = 5;
              const primaryLeft = Math.max(0, spotlightRect.left - padding);
              const primaryTop = Math.max(0, spotlightRect.top - padding);
              const primaryRight = Math.min(
                window.innerWidth,
                spotlightRect.right + padding
              );
              const primaryBottom = Math.min(
                window.innerHeight,
                spotlightRect.bottom + padding
              );

              // Create a polygon that covers everything except the spotlight
              const clipPathValue = `polygon(
                0% 0%, 
                0% 100%, 
                ${primaryLeft}px 100%, 
                ${primaryLeft}px ${primaryTop}px, 
                ${primaryRight}px ${primaryTop}px, 
                ${primaryRight}px ${primaryBottom}px, 
                ${primaryLeft}px ${primaryBottom}px, 
                ${primaryLeft}px 100%, 
                100% 100%, 
                100% 0%
              )`;

              return clipPathValue;
            })(),
          }}
        />

        {/* Spotlight border */}
        {spotlightRect && (
          <div
            className="absolute bg-transparent border-2 border-orange-500 rounded-lg"
            style={{
              position: "fixed",
              top: `${spotlightRect.top - 4}px`,
              left: `${spotlightRect.left - 4}px`,
              width: `${spotlightRect.width + 8}px`,
              height: `${spotlightRect.height + 8}px`,
              pointerEvents: "none",
              zIndex: 10001,
            }}
          />
        )}
      </motion.div>

      {/* Tooltip */}
      <motion.div
        key="tooltip"
        data-tour-tooltip
        className="bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-2xl border border-blue-200 p-8 w-96 max-w-md pointer-events-auto"
        style={(() => {
          const position = getTooltipPosition();
          return {
            position: "fixed" as const,
            zIndex: 10003,
            ...position,
          };
        })()}
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{
          opacity: spotlightRect && !isTransitioning ? 1 : 0,
          scale: spotlightRect && !isTransitioning ? 1 : 0.9,
          y: spotlightRect && !isTransitioning ? 0 : 20,
        }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{
          duration: 0.4,
          ease: "easeOut",
        }}
      >
        {/* Header */}
        <div className="flex flex-col items-center mb-6">
          {tourState.currentTourId &&
            (() => {
              const tourInfo = getTourInfo(tourState.currentTourId);
              return tourInfo ? (
                <span className="text-sm font-medium text-gray-700">
                  {tourInfo.name}
                </span>
              ) : null;
            })()}
          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white shadow-lg mb-4">
            <LuCheck className="h-6 w-6" />
          </div>
        </div>

        {/* Content */}
        <motion.div
          className="mb-8 text-center"
          initial={{ opacity: 0 }}
          animate={{
            opacity: spotlightRect && !isTransitioning ? 1 : 0,
          }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <h3 className="text-xl font-bold text-gray-900 mb-3">
            {currentStep.title}
          </h3>
          <p className="text-gray-600 text-base leading-relaxed">
            {currentStep.content}
          </p>

          {/* Detalles expandibles */}
          {currentStep.details && (
            <div className="mt-4">
              <motion.div
                className="overflow-hidden"
                initial={false}
                animate={{
                  height: isDetailsExpanded ? "auto" : 0,
                  opacity: isDetailsExpanded ? 1 : 0,
                }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                style={{ maxHeight: isDetailsExpanded ? "200px" : "0px" }}
              >
                <div className="overflow-y-auto" style={{ maxHeight: "200px" }}>
                  <p className="text-gray-500 text-sm leading-relaxed pt-3 border-t border-gray-200">
                    {currentStep.details}
                  </p>
                </div>
              </motion.div>

              <button
                onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium mt-3 transition-colors duration-200"
              >
                {isDetailsExpanded ? "Comprimir" : "Saber más"}
              </button>
            </div>
          )}
        </motion.div>

        {/* Action */}
        <motion.div
          className="flex justify-center"
          initial={{ opacity: 0 }}
          animate={{
            opacity: spotlightRect && !isTransitioning ? 1 : 0,
          }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <Button
            onClick={completeTour}
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-8 py-3 rounded-xl font-semibold text-base shadow-lg hover:shadow-xl transition-all duration-200"
          >
            <LuCheck className="h-5 w-5 mr-2" />
            Entendido
          </Button>
        </motion.div>
      </motion.div>

      {/* Global pointer-events blocker - must be last to be on top */}
      <div
        className="fixed inset-0 z-[10002] pointer-events-auto"
        style={{
          pointerEvents: "auto",
          backgroundColor: "rgba(0, 0, 0, 0.001)",
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      />
    </AnimatePresence>
  );
};
